import { HttpErrorResponse } from '@angular/common/http';
import { SESSION_KEYS, ERROR_MESSAGES } from '../constants/auth.constants';
import { PendingError, ErrorInfo } from '../models/auth.models';
import { AuthLogger } from './logger.utils';

export function extractErrorInfo(error: unknown): ErrorInfo {
    let errorCode: string = 'token_exchange_failed';
    let errorMessage: string = 'We couldn\'t complete your sign-in. Please try again.';

    if (error && typeof error === 'object') {
        const err = error as any;

        if (err instanceof HttpErrorResponse) {
            ({ errorCode, errorMessage } = mapHttpError(err));
        }
        else if (err.error || err.error_description) {
            errorCode = err.error || errorCode;
            errorMessage = err.error_description || err.message || errorMessage;
        }
    }

    return { code: errorCode, message: errorMessage };
}

function mapHttpError(error: HttpErrorResponse): { errorCode: string; errorMessage: string } {
    let errorCode: string = 'token_exchange_failed';
    let errorMessage: string = 'We couldn\'t complete your sign-in. Please try again.';

    // Check backend error object first
    if (error.error && typeof error.error === 'object') {
        errorCode = error.error.error || errorCode;
        errorMessage = error.error.error_description || error.error.message || errorMessage;
    }
    else if (error.status === 0) {
        errorCode = 'network_error';
        errorMessage = 'Unable to connect to our servers. Please check your internet connection and try again.';
    }
    else if (error.status === 401) {
        errorCode = 'invalid_grant';
        errorMessage = 'Your sign-in link has expired or already been used. Please sign in again.';
    }
    else if (error.status === 400) {
        errorCode = error.error?.error || 'invalid_request';
        errorMessage = 'There was a problem with your sign-in request. Please try again.';
    }
    else if (error.status >= 500) {
        errorCode = 'server_error';
        errorMessage = 'Our servers are having trouble right now. Please try again in a few moments.';
    }
    else if (error.status === 429) {
        errorCode = 'too_many_requests';
        errorMessage = 'Too many sign-in attempts. Please wait a few minutes before trying again.';
    }
    else {
        errorMessage = 'Something went wrong during sign-in. Please try again or contact support if this continues.';
    }

    return { errorCode, errorMessage };
}

export function storePendingError(errorType: string, message: string): void {
    const errorData: PendingError = {
        error: errorType,
        description: message,
        timestamp: Date.now()
    };
    sessionStorage.setItem(SESSION_KEYS.PENDING_AUTH_ERROR, JSON.stringify(errorData));
}

export function getPendingError(): PendingError | null {
    const pendingErrorStr = sessionStorage.getItem(SESSION_KEYS.PENDING_AUTH_ERROR);
    if (!pendingErrorStr) return null;

    try {
        const error = JSON.parse(pendingErrorStr) as PendingError;
        sessionStorage.removeItem(SESSION_KEYS.PENDING_AUTH_ERROR);
        return error;
    } catch (e) {
        AuthLogger.error('[Auth] Failed to parse pending error:', e);
        sessionStorage.removeItem(SESSION_KEYS.PENDING_AUTH_ERROR);
        return null;
    }
}

export function clearStoredErrors(): void {
    localStorage.removeItem('universal_login_error');
    localStorage.removeItem('auth_error');
    sessionStorage.removeItem('AUTH_ERROR');
    sessionStorage.removeItem(SESSION_KEYS.PENDING_AUTH_ERROR);
}

export function hasAuthErrorInUrl(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return !!(urlParams.get('auth_error') || urlParams.get('error'));
}

export function extractAuthErrorFromUrl(): { error: string; description: string } | null {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('auth_error') || urlParams.get('error');
    const description = urlParams.get('auth_error_description') || urlParams.get('error_description');

    if (!error) return null;

    return {
        error,
        description: description || ERROR_MESSAGES.AUTH_FAILED
    };
}